import React, { useState, useEffect } from 'react';
import { format, subDays } from 'date-fns';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Bar, Legend, Area, ComposedChart
} from 'recharts';
import { getAllLogs } from '../db/database';
import { calculateTNSS, calculateTOSS, calculateTotalSymptomScore } from '../utils/helpers';
import { SYMPTOM_LABELS } from '../types';
import type { DailyLog } from '../types';

type Period = '7d' | '30d' | '90d' | '1y' | 'all';

export default function Trends() {
  const [logs, setLogs] = useState<DailyLog[]>([]);
  const [period, setPeriod] = useState<Period>('30d');
  const [chartType, setChartType] = useState<'vas' | 'symptoms' | 'pollen' | 'context'>('vas');

  useEffect(() => {
    getAllLogs().then(setLogs);
  }, []);

  const periodDays: Record<Period, number> = { '7d': 7, '30d': 30, '90d': 90, '1y': 365, 'all': 99999 };
  const cutoff = format(subDays(new Date(), periodDays[period]), 'yyyy-MM-dd');
  const filteredLogs = period === 'all' ? logs : logs.filter(l => l.date >= cutoff);

  if (filteredLogs.length === 0) {
    return (
      <div className="page">
        <h1 className="page-title">Tendenze</h1>
        <div className="empty-state">
          <h3>Dati insufficienti</h3>
          <p>Registra i tuoi sintomi per almeno qualche giorno per visualizzare i trend.</p>
        </div>
      </div>
    );
  }

  // Prepare chart data
  const chartData = filteredLogs.map(log => {
    // Calculate 7-day rolling average
    const idx = logs.indexOf(log);
    const window = logs.slice(Math.max(0, idx - 6), idx + 1);
    const avg7 = window.reduce((s, l) => s + l.vasScore, 0) / window.length;

    return {
      date: format(new Date(log.date), 'dd/MM'),
      fullDate: log.date,
      vas: log.vasScore,
      vas7avg: Math.round(avg7 * 10) / 10,
      tnss: calculateTNSS(log),
      toss: calculateTOSS(log),
      total: calculateTotalSymptomScore(log),
      pollenGrass: log.pollenGrass,
      pollenBirch: log.pollenBirch,
      pollenOlive: log.pollenOlive,
      pollenMugwort: log.pollenMugwort,
      pollenRagweed: log.pollenRagweed,
      temp: log.temperature,
      humidity: log.humidity,
      pm25: log.pm25,
      sleepHours: log.sleepHours,
      stressLevel: log.stressLevel,
    };
  });

  return (
    <div className="page">
      <h1 className="page-title">Tendenze</h1>

      {/* Period selector */}
      <div className="tabs">
        {(['7d', '30d', '90d', '1y', 'all'] as Period[]).map(p => (
          <button key={p} className={`tab ${period === p ? 'active' : ''}`} onClick={() => setPeriod(p)}>
            {p === 'all' ? 'Tutto' : p}
          </button>
        ))}
      </div>

      {/* Chart type selector */}
      <div className="tabs">
        {([
          ['vas', 'VAS'],
          ['symptoms', 'Sintomi'],
          ['pollen', 'Pollini'],
          ['context', 'Contesto'],
        ] as [string, string][]).map(([key, label]) => (
          <button key={key} className={`tab ${chartType === key ? 'active' : ''}`}
            onClick={() => setChartType(key as any)}>
            {label}
          </button>
        ))}
      </div>

      {/* VAS trend */}
      {chartType === 'vas' && (
        <div className="card">
          <div className="card-title">Punteggio VAS giornaliero + media 7 giorni</div>
          <ResponsiveContainer width="100%" height={250}>
            <ComposedChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'var(--text-muted)' }} interval="preserveStartEnd" />
              <YAxis domain={[0, 10]} tick={{ fontSize: 10, fill: 'var(--text-muted)' }} />
              <Tooltip
                contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }}
                labelStyle={{ color: 'var(--text-primary)' }}
              />
              <Area type="monotone" dataKey="vas" fill="var(--accent)" fillOpacity={0.1} stroke="var(--accent)" strokeWidth={1} dot={{ r: 2 }} />
              <Line type="monotone" dataKey="vas7avg" stroke="var(--warning)" strokeWidth={2} dot={false} name="Media 7gg" />
            </ComposedChart>
          </ResponsiveContainer>

          {/* Summary stats */}
          <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: 12 }}>
            <StatBox label="Media" value={(filteredLogs.reduce((s, l) => s + l.vasScore, 0) / filteredLogs.length).toFixed(1)} />
            <StatBox label="Max" value={Math.max(...filteredLogs.map(l => l.vasScore)).toString()} />
            <StatBox label="Min" value={Math.min(...filteredLogs.map(l => l.vasScore)).toString()} />
            <StatBox label="Giorni" value={filteredLogs.length.toString()} />
          </div>
        </div>
      )}

      {/* Symptoms breakdown */}
      {chartType === 'symptoms' && (
        <div className="card">
          <div className="card-title">TNSS e TOSS nel tempo</div>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'var(--text-muted)' }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 10, fill: 'var(--text-muted)' }} />
              <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="tnss" stroke="#4fc3f7" strokeWidth={2} name="TNSS (nasale)" dot={{ r: 2 }} />
              <Line type="monotone" dataKey="toss" stroke="#ff7043" strokeWidth={2} name="TOSS (oculare)" dot={{ r: 2 }} />
              <Line type="monotone" dataKey="total" stroke="#ab47bc" strokeWidth={2} name="Totale" dot={{ r: 2 }} />
            </LineChart>
          </ResponsiveContainer>

          {/* Symptom frequency ranking */}
          <div style={{ marginTop: 16 }}>
            <div className="card-title">Classifica sintomi per frequenza</div>
            {(() => {
              const freq: Record<string, number> = {};
              for (const log of filteredLogs) {
                for (const [key] of Object.entries(SYMPTOM_LABELS)) {
                  if ((log as any)[key] > 0) {
                    freq[key] = (freq[key] || 0) + 1;
                  }
                }
              }
              const sorted = Object.entries(freq).sort((a, b) => b[1] - a[1]);
              if (sorted.length === 0) return <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Nessun sintomo registrato</div>;
              const maxCount = sorted[0][1];
              return sorted.slice(0, 8).map(([key, count]) => (
                <div key={key} style={{ marginBottom: 6 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 2 }}>
                    <span>{SYMPTOM_LABELS[key]}</span>
                    <span style={{ color: 'var(--text-muted)' }}>{count}/{filteredLogs.length} giorni</span>
                  </div>
                  <div style={{ height: 6, background: 'var(--bg-input)', borderRadius: 3 }}>
                    <div style={{ height: '100%', borderRadius: 3, background: 'var(--accent)', width: `${(count / maxCount) * 100}%` }} />
                  </div>
                </div>
              ));
            })()}
          </div>
        </div>
      )}

      {/* Pollen overlay */}
      {chartType === 'pollen' && (
        <div className="card">
          <div className="card-title">VAS vs. Pollini (overlay)</div>
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'var(--text-muted)' }} interval="preserveStartEnd" />
              <YAxis yAxisId="vas" domain={[0, 10]} tick={{ fontSize: 10, fill: 'var(--text-muted)' }} />
              <YAxis yAxisId="pollen" orientation="right" tick={{ fontSize: 10, fill: 'var(--text-muted)' }} />
              <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line yAxisId="vas" type="monotone" dataKey="vas" stroke="var(--accent)" strokeWidth={2} name="VAS" dot={{ r: 2 }} />
              <Bar yAxisId="pollen" dataKey="pollenGrass" fill="#66bb6a" opacity={0.6} name="Graminacee" />
              <Bar yAxisId="pollen" dataKey="pollenBirch" fill="#a5d6a7" opacity={0.6} name="Betulla" />
              <Bar yAxisId="pollen" dataKey="pollenOlive" fill="#81c784" opacity={0.6} name="Olivo" />
              <Bar yAxisId="pollen" dataKey="pollenMugwort" fill="#ffcc80" opacity={0.6} name="Artemisia" />
              <Bar yAxisId="pollen" dataKey="pollenRagweed" fill="#ef9a9a" opacity={0.6} name="Ambrosia" />
            </ComposedChart>
          </ResponsiveContainer>
          {!chartData.some(d => d.pollenGrass || d.pollenBirch || d.pollenOlive) && (
            <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', marginTop: 8 }}>
              Configura la posizione nelle Impostazioni per i dati sui pollini automatici.
            </div>
          )}
        </div>
      )}

      {/* Context correlations */}
      {chartType === 'context' && (
        <div className="card">
          <div className="card-title">Sonno, Stress e Sintomi</div>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'var(--text-muted)' }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 10, fill: 'var(--text-muted)' }} />
              <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="vas" stroke="var(--accent)" strokeWidth={2} name="VAS" dot={{ r: 2 }} />
              <Line type="monotone" dataKey="sleepHours" stroke="#66bb6a" strokeWidth={2} name="Ore sonno" dot={{ r: 2 }} connectNulls />
              <Line type="monotone" dataKey="stressLevel" stroke="#ef5350" strokeWidth={2} name="Stress" dot={{ r: 2 }} connectNulls />
            </LineChart>
          </ResponsiveContainer>

          {/* Environmental correlations */}
          <div style={{ marginTop: 16 }}>
            <div className="card-title">Correlazioni ambientali</div>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'var(--text-muted)' }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 10, fill: 'var(--text-muted)' }} />
                <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="vas" stroke="var(--accent)" strokeWidth={2} name="VAS" dot={{ r: 2 }} />
                <Line type="monotone" dataKey="temp" stroke="#ffa726" strokeWidth={1} name="Temp °C" dot={false} connectNulls />
                <Line type="monotone" dataKey="humidity" stroke="#42a5f5" strokeWidth={1} name="Umidita' %" dot={false} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Year-over-year comparison hint */}
      {logs.length > 365 && (
        <div className="card">
          <div className="card-title">Confronto anno su anno</div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            Hai dati sufficienti per confrontare i periodi anno su anno. I pattern allergici stagionali tendono a ripetersi!
          </div>
        </div>
      )}
    </div>
  );
}

function StatBox({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 20, fontWeight: 700 }}>{value}</div>
      <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{label}</div>
    </div>
  );
}
